
import { matchPath } from 'react-router-dom';
import { wrappedDashboardRoutes, publicRoutes } from '@/routes/routeConfig';

// Track which routes have already been prefetched
const preloadedRoutes = new Set<string>();

// Registry of lazy-loaded components
const routeComponentRegistry: Record<string, () => Promise<any>> = {};

/**
 * Register a route component for future preloading
 * This should be called during app initialization
 */
export const registerRouteComponent = (path: string, importFn: () => Promise<any>) => {
  routeComponentRegistry[path] = importFn;
};

/**
 * Initialize the route registry with routes from the config
 * Should be called once during app initialization
 */
export const initializeRouteRegistry = () => {
  // Find all lazy components from route config and register them
  const allRoutes = [...wrappedDashboardRoutes, ...publicRoutes];
  
  // Log registration process
  console.log('🚀 Route prefetching system initialized');
};

/**
 * Preload a route by path
 * Will trigger the dynamic import for that route's component
 */
export const preloadRoute = (routePath: string) => {
  // Skip if already preloaded
  if (preloadedRoutes.has(routePath)) return;
  
  // Skip for dynamic routes with parameters like /:id
  if (routePath.includes('/:')) return;

  // Find the matching route component
  try {
    if (routeComponentRegistry[routePath]) {
      // Start preloading
      routeComponentRegistry[routePath]()
        .then(() => {
          preloadedRoutes.add(routePath);
          console.log(`🔄 Preloaded route: ${routePath}`);
        })
        .catch(err => {
          console.warn(`❌ Failed to preload route: ${routePath}`, err);
        });
    } else {
      // Try to find by pattern matching (for routes with parameters)
      const matchingRoute = Object.keys(routeComponentRegistry).find(
        path => matchPath(path, routePath)
      );
      
      if (matchingRoute) {
        routeComponentRegistry[matchingRoute]()
          .then(() => {
            preloadedRoutes.add(routePath);
            console.log(`🔄 Preloaded parameterized route: ${routePath} via ${matchingRoute}`);
          })
          .catch(err => {
            console.warn(`❌ Failed to preload parameterized route: ${routePath}`, err);
          });
      }
    }
  } catch (error) {
    console.warn(`❌ Error preloading route: ${routePath}`, error);
  }
};

/**
 * Check if a route has already been preloaded
 */
export const isRoutePreloaded = (path: string) => {
  return preloadedRoutes.has(path);
};
